import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Form, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import CryptoJS from 'crypto-js';

function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Email cannot be empty.');
      return;
    }
    setLoading(true);
    try {
      const url = `${process.env.REACT_APP_BASE_URI}/workflow/request_otp/`;
      const response = await fetch(url, {
        method: 'POST',
        body: JSON.stringify({ 'email': email }),
        headers: {
          'Content-Type': 'application/json; charset=UTF-8',
        },
      });
      const data = await response.json();
      setLoading(false);
      if (data.success) {
        toast.success('Confirmation email sent! Please check your inbox.');
        setStep(2);
      } else {
        toast.error(data.message || 'An error occurred. Please try again.');
      }
    } catch (error) {
      setLoading(false);
      toast.error(error.message || 'An error occurred. Please try again.');
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    if (!otp.trim()) {
      toast.error('OTP cannot be empty.');
      return;
    }
    setLoading(true);
    try {
      const url = `${process.env.REACT_APP_BASE_URI}/workflow/verify_otp/`;
      const response = await fetch(url, {
        method: 'POST',
        body: JSON.stringify({ 'email': email,
                                'otp': otp }),
        headers: {
          'Content-Type': 'application/json; charset=UTF-8',
        },
      });
      const data = await response.json();
      setLoading(false);
      if (data.success) {
        toast.success('OTP verified! Please set your new password.');
        setStep(3); // Move to step 3 for password update
      } 
      else if(response.status == 403) {
        toast.error(data.message || 'Invalid OTP. Please try again.');
        navigate('/login'); 

      } else {
        toast.error(data.message || 'Invalid OTP. Please try again.');
      }
    } catch (error) {
      setLoading(false);
      toast.error(error.message || 'An error occurred. Please try again.');
    }
  };

  const validatePassword = (password) => {
    const minLength = 8;
    const maxLength = 20;
    const hasNumber = /\d/;
    const hasUppercase = /[A-Z]/;
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/;
    return (
      password.length >= minLength &&
      password.length <= maxLength &&
      hasUppercase.test(password) && 
      hasNumber.test(password) &&
      hasSpecialChar.test(password)
    );
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    if (!newPassword.trim() || !confirmPassword.trim()) {
      toast.error('Both fields are required.');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match.');
      return;
    }

    if (!validatePassword(newPassword)) {
      toast.error('Password must be at least 8 characters long and include at least 1 uppercase character, 1 number and 1 special character.');
      return;
    }
    
    setLoading(true);
    try {
      const url = `${process.env.REACT_APP_BASE_URI}/workflow/reset_password/`;
      const hashedPassword = CryptoJS.SHA256(newPassword).toString();
        
      const response = await fetch(url, {
        method: 'POST',
        body: JSON.stringify({ 'email': email, 
                                'otp': otp,
                                'password': hashedPassword }),
        headers: {
          'Content-Type': 'application/json; charset=UTF-8',
        },
      });
      const data = await response.json();
      setLoading(false);
      if (data.success) {
        toast.success('Password updated successfully! Please log in.');
        navigate('/login'); 
      } else {
        toast.error(data.message || 'An error occurred. Please try again.');
      }
    } catch (error) {
      setLoading(false);
      toast.error(error.message || 'An error occurred. Please try again.');
    }
  };

  const [typewriterText, setTypewriterText] = useState([]);
  const speed = 100;

  const typeWriter = (text, index) => {
    if (index < text.length) {
      setTypewriterText((prev) => [...prev, text.charAt(index)]);
      setTimeout(() => typeWriter(text, index + 1), speed);
    }
  };

  useEffect(() => {
    if (window.location.pathname === '/forgot-password') {
      setTypewriterText([]);
      const texts = [
        'Welcome to \n',
        'Cygnus Accounting Hub \n',
        'For best experience please use Chrome, Firefox or Edge browser.',
      ];
      texts.forEach((text, idx) => {
        setTimeout(() => typeWriter(text, 0), idx * 3000);
      });
    }
  }, [window.location.pathname]);

  return (
    <Container fluid>
      <Row>
        <Col className="login-drop">
          <span>{typewriterText.join('')}</span>
        </Col>
        <Col style={{ background: "#F0F0F0" }}>
          <div style={{ margin: "25% 5%", background: "#F0F0F0" }}>
            <div style={{ marginBottom: "5%" }}>
              <h3>{step === 1 ? 'Forgot Password' : step === 2 ? 'Enter OTP' : 'Set New Password'}</h3>
            </div>
            <Form onSubmit={step === 1 ? handleEmailSubmit : step === 2 ? handleOtpSubmit : handlePasswordUpdate}>
              {step === 1 ? (
                <>
                  <Form.Group className="mb-3" controlId="formBasicEmail">
                    <Form.Label>Enter your registered email address</Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="Email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </Form.Group>
                </>
              ) : step === 2 ? (
                <>
                  <Form.Group className="mb-3" controlId="formBasicOtp">
                    <Form.Label>Enter the OTP sent to your email</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="OTP"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                    />
                  </Form.Group>
                </>
              ) : (
                <>
                  <Form.Group className="mb-3" controlId="formBasicNewPassword">
                    <Form.Label>New Password</Form.Label>
                    <Form.Control
                      type="password"
                      placeholder="New Password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3" controlId="formBasicConfirmPassword">
                    <Form.Label>Confirm Password</Form.Label>
                    <Form.Control
                      type="password"
                      placeholder="Confirm Password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                  </Form.Group>
                </>
              )}
              <Button
                variant="primary"
                type="submit"
                disabled={loading}
                className="custom-button"
              >
                {loading ? (step === 1 ? 'Sending...' : step === 2 ? 'Verifying...' : 'Updating...') : (step === 1 ? 'Submit Reset Request' : step === 2 ? 'Verify OTP' : 'Update Password')}
              </Button>
            </Form>
          </div>
        </Col>
      </Row>
    </Container>
  );
}

export default ForgotPassword;
